import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    Button,
    Typography,
    Box,
    Alert,
    Switch,
    FormControlLabel,
    CircularProgress
} from '@mui/material';
import NotificationsIcon from '@mui/icons-material/Notifications';
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone';
import { useAddToHomeScreenSupport, usePWAStatus } from '../../hooks/usePWAStatus';
import { notifications, runtime } from '../../platform';

/**
 * Push Notification Setup Component
 * Handles push notification permissions and subscription management
 */
const PushNotificationSetup = ({ open, onClose }) => {
    const [pushEnabled, setPushEnabled] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [hasSubscription, setHasSubscription] = useState(false);
    const [permission, setPermission] = useState('default');
    
    const { isStandalone } = usePWAStatus();
    const isAppShell = runtime.isNative() || isStandalone;
    const isAddToHomeScreenSupported = useAddToHomeScreenSupport();

    useEffect(() => {
        if (!open) return undefined;

        void checkPushStatus();
        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') void checkPushStatus();
        };
        document.addEventListener('visibilitychange', handleVisibilityChange);
        return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
    }, [open]);

    const checkPushStatus = async () => {
        try {
            const status = await notifications.getStatus();
            setHasSubscription(status.subscription !== null);
            setPushEnabled(status.enabled);
            setPermission(status.permission);
        } catch (error) {
            console.error('Error checking push status:', error);
        }
    };

    const handleTogglePush = async () => {
        if (pushEnabled) {
            await disablePushNotifications();
        } else {
            await enablePushNotifications();
        }
    };

    const enablePushNotifications = async () => {
        setLoading(true);
        setError(null);
        setSuccess(null);

        try {
            const result = runtime.isNative()
                ? (pushEnabled ? (await notifications.sendTest(), { testAccepted: true }) : await notifications.enable({ sendTest: true }))
                : await notifications.enable();

            setPushEnabled(true);
            setHasSubscription(true);
            setPermission('granted');
            setSuccess('Push notifications enabled successfully!');
            if (runtime.isNative()) {
                if (result?.testAccepted === false) {
                    setError('Notifications are enabled, but the test could not be sent. You can retry the test.');
                } else onClose();
            }
        } catch (error) {
            console.warn('Unable to enable push notifications.');
            setError(error.response?.data?.error || error.message || 'Failed to enable push notifications');
        } finally {
            setLoading(false);
        }
    };

    const disablePushNotifications = async () => {
        setLoading(true);
        setError(null);
        setSuccess(null);

        try {
            await notifications.disable();

            setPushEnabled(false);
            setHasSubscription(false);
            setSuccess('Push notifications disabled successfully!');
        } catch (error) {
            console.warn('Unable to disable push notifications.');
            setError(error.message || 'Failed to disable push notifications');
        } finally {
            setLoading(false);
        }
    };

    const handleTestNotification = async () => {
        setLoading(true);
        setError(null);
        setSuccess(null);

        try {
            await notifications.sendTest();
            setSuccess('Test notification sent! Check your device.');
        } catch (error) {
            console.warn('Unable to send test notification.');
            setError(error.response?.data?.error || error.message || 'Failed to send test notification');
        } finally {
            setLoading(false);
        }
    };

    const isSupported = notifications.isSupported();

    if (runtime.isNative()) {
        return <Dialog open={open} onClose={() => { if (!loading) onClose(); }}
            aria-labelledby="native-push-setup-title" aria-describedby="native-push-setup-description">
            <DialogTitle id="native-push-setup-title" sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <NotificationsIcon color="primary" sx={{ fontSize: 28, flexShrink: 0 }} />
                <Box component="span">Enable push notifications?</Box>
            </DialogTitle>
            <DialogContent>
                <DialogContentText id="native-push-setup-description">
                    Receive important alerts on this device even when the app is closed.
                    You can change this in your notification settings.
                </DialogContentText>
                {!isSupported && <Alert severity="warning" sx={{ mt: 2 }}>
                    Push notifications are not configured in this app build.
                </Alert>}
                {permission === 'denied' && <Alert severity="warning" sx={{ mt: 2 }}>
                    Push notifications are disabled for Connect App 365 in this device's system settings.
                    Enable notifications there, then return to the app and try again.
                </Alert>}
                {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
            </DialogContent>
            <DialogActions>
                <Button disabled={loading} onClick={onClose}>Not now</Button>
                <Button disabled={loading || !isSupported} onClick={enablePushNotifications}>{pushEnabled ? 'Retry test' : 'Enable'}</Button>
            </DialogActions>
        </Dialog>;
    }

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>
                <Box display="flex" alignItems="center" gap={1}>
                    <NotificationsIcon />
                    Enable Push Notifications
                </Box>
            </DialogTitle>
            
            <DialogContent>
                {!isSupported && (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                        {runtime.isNative() ? 'Push notifications are not configured in this app build.' : 'Push notifications are not supported in this browser.'}
                    </Alert>
                )}

                {isSupported && permission === 'denied' && (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                        Push notifications are disabled for Connect App 365 in this device's system settings.
                        Enable notifications there, then return to the app and try again.
                    </Alert>
                )}

                {!isAppShell && isAddToHomeScreenSupported && (
                    <Alert severity="info" sx={{ mb: 2 }}>
                        <Box display="flex" alignItems="center" gap={1}>
                            <PhoneIphoneIcon />
                            <Typography variant="body2">
                                For the best notification experience, add this app to your home screen first.
                            </Typography>
                        </Box>
                    </Alert>
                )}


                <Typography variant="body1" sx={{ mb: 2 }}>
                    Enable push notifications to receive important alerts even when the app is closed.
                </Typography>

                <Box sx={{ mb: 2 }}>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={pushEnabled}
                                onChange={handleTogglePush}
                                disabled={!isSupported || loading}
                            />
                        }
                        label={pushEnabled
                            ? 'Push notifications enabled'
                            : permission === 'denied'
                                ? 'Disabled in device settings'
                                : 'Push notifications disabled'}
                    />
                </Box>

                {pushEnabled && hasSubscription && (
                    <Box sx={{ mb: 2 }}>
                        <Button
                            variant="outlined"
                            onClick={handleTestNotification}
                            disabled={loading}
                            startIcon={loading ? <CircularProgress size={16} /> : <NotificationsIcon />}
                        >
                            Test Notification
                        </Button>
                    </Box>
                )}

                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}

                {success && (
                    <Alert severity="success" sx={{ mb: 2 }}>
                        {success}
                    </Alert>
                )}
            </DialogContent>

            <DialogActions>
                <Button onClick={onClose}>
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default PushNotificationSetup;
